import { Collapse, Descriptions, Flex, List, Typography } from 'antd';
import { useMemo } from 'react';

import { useAdminPersonsController_findAll } from '@api-client/generated/AdminPersonsController/findAll';
import { Schemas } from '@api-client/generated/types';
import { complianceCompanyCaseUrl } from '@utils';

import { CompanyDetails } from './sections/CompanyDetails';
import { CompanyInfo } from './sections/CompanyInfo';
import { CompanyOwners } from './sections/CompanyOwners';
import { CompanyStructure } from './sections/CompanyStructure';
import * as S from './styled';
import { CollapseItems } from './types';

type OnboardingCardProps = {
  data: Schemas.Incorporation;
  companyId: string;
  /** Decides whether the compliance case link is rendered — see `canBeInCompliance`. */
  companyStatus?: Schemas.AdminCompanyResponseDto['status'];
};

const { Title, Text } = Typography;

/**
 * The onboarding record, read-only.
 *
 * Every form and action this card used to carry is gone: company details, KYC, adding and removing
 * a shareholder, minting a KYC link, assigning a UBO number. The first four wrote client data that
 * master-data-service is taking ownership of; the last two were ours, but they still wrote to a
 * person record the backoffice no longer owns.
 *
 * There is no MDS link on this tab, unlike Company info: MDS mints its own ids for persons and
 * keeps the legacy id in `easybiz_sync`'s private id-map, so there is no person URL to build from
 * what the backoffice holds.
 */
export const OnboardingCard = (props: OnboardingCardProps) => {
  const retoolUrl = `https://easybiz.retool.com/apps/6d729d7a-5264-11f0-be02-ffb712a54afc/Ops/company-onboarding?id=${props.companyId}`;

  // Where this record is actually edited now that the tab is read-only. Not a per-person link:
  // compliance keys persons by MDS's own UUIDv7, which the backoffice does not hold, so the case
  // page is the deepest link the shared company id can address.
  // Judged by the company's status, not by having an onboarding record: a company can have one
  // and still be a prospect, which MDS's importer does not take — 239 such companies on the
  // current data — so the case link would 404 for every one of them.
  const complianceUrl = complianceCompanyCaseUrl(
    props.companyId,
    props.companyStatus
  );

  const { data: persons = [], isPending: isLoadingPersons } =
    useAdminPersonsController_findAll({
      params: { companyId: props.companyId },
    });

  const companyItself = useMemo(
    () => persons.find((person) => person.id === props.data.id),
    [props.data, persons]
  );

  const renderFiles = (fileType: string) => {
    const files = companyItself?.files.filter((file) => file.type === fileType);

    if (!files?.length) {
      return null;
    }

    return (
      <Descriptions
        layout="vertical"
        items={[
          {
            key: 'files',
            label: `Files (${fileType})`,
            children: (
              <List>
                {files.map((file) => (
                  <List.Item key={file.id}>
                    <Flex gap={16}>
                      <Text>{file.name}</Text>
                      <a href={file.url} download={file.name} target="_blank">
                        Download
                      </a>
                    </Flex>
                  </List.Item>
                ))}
              </List>
            ),
          },
        ]}
        column={1}
      />
    );
  };

  return (
    <>
      <S.Container>
        {complianceUrl && (
          <S.NoteMessage>
            This record is read-only here. The cap table, the UBO answers and
            the KYC decisions are worked on in Compliance:{' '}
            <a href={complianceUrl} target="_blank" rel="noopener noreferrer">
              open this company&apos;s case
            </a>
          </S.NoteMessage>
        )}

        <S.NoteMessage>
          Retool link:{' '}
          <a href={retoolUrl} target="_blank" rel="noopener noreferrer">
            {retoolUrl}
          </a>
        </S.NoteMessage>
        <Collapse
          size="large"
          defaultActiveKey={[
            CollapseItems.COMPANY_DETAILS,
            CollapseItems.COMPANY_INFO,
            CollapseItems.COMPANY_STRUCTURE,
            CollapseItems.OWNERS,
          ]}
          items={[
            {
              key: CollapseItems.COMPANY_DETAILS,
              label: <Title level={5}>Company details</Title>,
              children: <CompanyDetails data={props.data} />,
            },
            {
              key: CollapseItems.COMPANY_INFO,
              label: <Title level={5}>Company info</Title>,
              children: companyItself && (
                <>
                  {renderFiles('proof_of_residence')}
                  {renderFiles('commercial_register')}
                  {renderFiles('article_of_incorporation_or_association')}
                  {renderFiles('shareholder_register')}
                  {renderFiles('chart_with_ownership_and_percents')}
                  {renderFiles('last_tax_return_with_annex')}
                  <CompanyInfo
                    incorporation={props.data}
                    companyItself={companyItself}
                  />
                </>
              ),
            },
            {
              key: CollapseItems.COMPANY_STRUCTURE,
              label: <Title level={5}>Company structure</Title>,
              children: (
                <CompanyStructure
                  persons={persons}
                  isLoading={isLoadingPersons}
                />
              ),
            },
            {
              key: CollapseItems.OWNERS,
              label: <Title level={5}>Owners</Title>,
              children: (
                <CompanyOwners
                  companyId={props.companyId}
                  persons={persons}
                  isLoading={isLoadingPersons}
                />
              ),
            },
          ]}
        />
      </S.Container>
    </>
  );
};
